import {
  parseKmlCoordTriplet
} from './portal-kml-utils.js'
import {
  renderKmlActionsEditor
} from './portal-editors-kml.js'
import {
  renderKmzActionGroupsEditor
} from './portal-editors-kmz.js'
import {
  renderPropEditorRows
} from './portal-editors-props.js'
import {
  getMissionAltitudeContextFor3d,
  isEllipsoidHeightPath,
  isWaypointHeightPath,
  syncPointAltitudeFields
} from './portal-altitude-utils.js'
import {
  xmlToObj,
  getMarkerStyle,
  setMarkerStyle,
  updatePointXmlByPath,
  updateXmlByPath
} from './portal-point-map-utils.js'
import {
  PORTAL_PRESETS,
  applyPortalPresetDefaultsToFlatMap
} from './portal-presets.js'
import {
  refreshWaypointMarker,
  setByPath
} from './portal-waypoint-core-utils.js'
import {
  startPortalLoadingTask
} from './portal-loading-widget.js'
import {
  applyKmzResolvedHeadingDisplayValuesToMap,
  isKmzHeadingFieldPath,
  isKmzGlobalHeadingFieldPath,
  syncKmzPointHeadingScopesFromXml,
  syncKmzMissionHeadingScopesFromXml
} from './portal-kmz-heading-utils.js'
import {
  isKmzSpeedFieldPath,
  isKmzGlobalSpeedFieldPath,
  applyKmzResolvedSpeedDisplayValuesToMap,
  syncKmzPointSpeedScopesFromXml,
  syncKmzMissionSpeedScopesFromXml
} from './portal-kmz-speed-utils.js'
import {
  isKmzGimbalFieldPath,
  isKmzGlobalGimbalFieldPath,
  applyKmzResolvedGimbalDisplayValuesToMap,
  syncKmzPointGimbalScopesFromXml,
  syncKmzMissionGimbalScopesFromXml
} from './portal-kmz-gimbal-utils.js'
import {
  isKmzPayloadFieldPath,
  isKmzGlobalPayloadFieldPath,
  applyKmzResolvedPayloadDisplayValuesToMap,
  syncKmzPointPayloadScopesFromXml,
  syncKmzMissionPayloadScopesFromXml
} from './portal-kmz-payload-utils.js'
import {
  isKmzTurnFieldPath,
  isKmzGlobalTurnFieldPath,
  applyKmzResolvedTurnDisplayValuesToMap,
  syncKmzPointTurnScopesFromXml,
  syncKmzMissionTurnScopesFromXml
} from './portal-kmz-turn-utils.js'
import {
  isKmzStraightLineFieldPath,
  isKmzGlobalStraightLineFieldPath,
  applyKmzResolvedStraightLineDisplayValuesToMap,
  syncKmzPointStraightLineScopesFromXml,
  syncKmzMissionStraightLineScopesFromXml
} from './portal-kmz-straight-line-utils.js'
import {
  isKmzAltitudeContextFieldPath,
  isKmzGlobalAltitudeContextFieldPath,
  resolveKmzAltitudeContextFieldKey,
  applyKmzResolvedAltitudeContextDisplayValuesToMap,
  syncKmzPointAltitudeScopesFromXml,
  syncKmzMissionAltitudeScopesFromXml
} from './portal-kmz-altitude-utils.js'

const syncVisibleKmzActionFileSuffixInputs = function ($root, nextName, $) {
  if (!$root || !$root.length) return
  const safeName = String(nextName === undefined || nextName === null ? '' : nextName)
  $root.find('[data-kmz-action-param-line]').each(function () {
    const $line = $(this)
    const $key = $line.find('[data-kmz-action-param-key]').first()
    const $val = $line.find('[data-kmz-action-param-value]').first()
    if (!$key.length || !$val.length) return
    if (String($key.val() || '').trim() !== 'fileSuffix') return
    if (String($val.val() || '') === safeName) return
    $val.val(safeName)
    $val.trigger('input')
  })
}

const INTERNAL_POINT_NAME_PATH = 'project.pointName'

const syncKmzHeadingScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionHeadingScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointHeadingScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const resolveKmzHeadingEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzHeadingXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzHeadingEditBinding(path)
  const normalizedPath = binding.normalizedPath
  if (!normalizedPath) return false
  const isGlobal = isKmzGlobalHeadingFieldPath(normalizedPath)
  const targets = []
  if (isGlobal) {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push(missionState.folderEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      if (missionState.secondaryFolderEl) targets.push(missionState.secondaryFolderEl)
    }
  } else {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (point && point.xmlEl) targets.push(point.xmlEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
      if (waylinesNode) targets.push(waylinesNode)
    }
  }
  let changed = false
  targets.forEach((targetNode) => {
    changed = updateXmlByPath(targetNode, normalizedPath, value, function () {}) || changed
  })
  return changed
}

const syncKmzSpeedScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionSpeedScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointSpeedScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const syncKmzGimbalScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionGimbalScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointGimbalScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const syncKmzPayloadScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionPayloadScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointPayloadScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const resolveKmzPayloadEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzPayloadXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzPayloadEditBinding(path)
  const normalizedPath = binding.normalizedPath
  if (!normalizedPath || !isKmzPayloadFieldPath(normalizedPath)) return false
  const targets = []
  if (!binding.scopeId || binding.scopeId === 'template') {
    if (missionState.folderEl) targets.push(missionState.folderEl)
  }
  if (!binding.scopeId || binding.scopeId === 'waylines') {
    if (missionState.secondaryFolderEl) targets.push(missionState.secondaryFolderEl)
  }
  let changed = false
  targets.forEach((targetNode) => {
    changed = updateXmlByPath(targetNode, normalizedPath, value, function () {}) || changed
  })
  return changed
}

const resolveKmzGimbalEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzGimbalXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzGimbalEditBinding(path)
  const normalizedPath = String(binding.normalizedPath || '').trim()
  if (!normalizedPath) return false
  const lowerPath = normalizedPath.toLowerCase()
  const isGlobal = isKmzGlobalGimbalFieldPath(normalizedPath)
  const targets = []

  if (isGlobal) {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push({ node: missionState.folderEl, path: 'wpml:gimbalPitchMode' })
    }
    if (binding.scopeId === 'waylines') {
      if (missionState.secondaryFolderEl) targets.push({ node: missionState.secondaryFolderEl, path: 'wpml:gimbalPitchMode' })
    }
  } else if (lowerPath === 'wpml:gimbalpitchangle' || lowerPath === 'gimbalpitchangle') {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (point && point.xmlEl) targets.push({ node: point.xmlEl, path: 'wpml:gimbalPitchAngle' })
    }
    if (binding.scopeId === 'waylines') {
      const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
      if (waylinesNode) {
        targets.push({ node: waylinesNode, path: 'wpml:waypointGimbalHeadingParam.wpml:waypointGimbalPitchAngle' })
      }
    }
  } else {
    const isWaylineGimbalPath = lowerPath.indexOf('wpml:waypointgimbalheadingparam') === 0 ||
      lowerPath.indexOf('waypointgimbalheadingparam') === 0
    if (isWaylineGimbalPath) {
      if (!binding.scopeId || binding.scopeId === 'waylines') {
        const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
        if (waylinesNode) targets.push({ node: waylinesNode, path: normalizedPath })
      }
      if (binding.scopeId === 'template' && point && point.xmlEl) {
        const isPitch = lowerPath.endsWith('waypointgimbalpitchangle')
        targets.push({ node: point.xmlEl, path: isPitch ? 'wpml:gimbalPitchAngle' : normalizedPath })
      }
    }
  }

  let changed = false
  targets.forEach((target) => {
    if (!target || !target.node || !target.path) return
    changed = updateXmlByPath(target.node, target.path, value, function () {}) || changed
  })
  return changed
}

const resolveKmzSpeedEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzSpeedXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzSpeedEditBinding(path)
  const normalizedPath = binding.normalizedPath
  if (!normalizedPath) return false
  const isGlobal = isKmzGlobalSpeedFieldPath(normalizedPath)
  const targets = []
  if (isGlobal) {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push(missionState.folderEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      if (missionState.secondaryFolderEl) targets.push(missionState.secondaryFolderEl)
    }
  } else {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (point && point.xmlEl) targets.push(point.xmlEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
      if (waylinesNode) targets.push(waylinesNode)
    }
  }
  let changed = false
  targets.forEach((targetNode) => {
    changed = updateXmlByPath(targetNode, normalizedPath, value, function () {}) || changed
  })
  return changed
}

const syncKmzTurnScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionTurnScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointTurnScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const resolveKmzTurnEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzTurnXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzTurnEditBinding(path)
  const normalizedPath = binding.normalizedPath
  if (!normalizedPath) return false
  const isGlobal = isKmzGlobalTurnFieldPath(normalizedPath)
  const targets = []
  if (isGlobal) {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push(missionState.folderEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      if (missionState.secondaryFolderEl) targets.push(missionState.secondaryFolderEl)
    }
  } else {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (point && point.xmlEl) targets.push(point.xmlEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
      if (waylinesNode) targets.push(waylinesNode)
    }
  }
  let changed = false
  targets.forEach((targetNode) => {
    changed = updateXmlByPath(targetNode, normalizedPath, value, function () {}) || changed
  })
  return changed
}

const syncKmzStraightLineScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionStraightLineScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointStraightLineScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const resolveKmzStraightLineEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzStraightLineXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzStraightLineEditBinding(path)
  const normalizedPath = binding.normalizedPath
  if (!normalizedPath) return false
  const isGlobal = isKmzGlobalStraightLineFieldPath(normalizedPath)
  const targets = []
  if (isGlobal) {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push(missionState.folderEl)
    }
  } else {
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (point && point.xmlEl) targets.push(point.xmlEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
      if (waylinesNode) targets.push(waylinesNode)
    }
  }
  let changed = false
  targets.forEach((targetNode) => {
    changed = updateXmlByPath(targetNode, normalizedPath, value, function () {}) || changed
  })
  return changed
}

const syncKmzAltitudeScopesForPoints = function (points, ctx, options) {
  if (!ctx || ctx.importMissionType !== 'kmz') return
  const opts = (options && typeof options === 'object') ? options : {}
  const missionState = ctx.importMissionState || buildFallbackMissionState(points)
  if (opts.global === true && missionState) {
    syncKmzMissionAltitudeScopesFromXml(missionState, xmlToObj)
    return
  }
  const templateFolderEl = missionState && missionState.folderEl ? missionState.folderEl : null
  const waylinesFolderEl = missionState && missionState.secondaryFolderEl ? missionState.secondaryFolderEl : null
  ;(Array.isArray(points) ? points : []).filter((point) => !!point).forEach((point) => {
    syncKmzPointAltitudeScopesFromXml(point, {
      fnXmlToObj: xmlToObj,
      templateFolderEl: templateFolderEl,
      waylinesFolderEl: waylinesFolderEl
    })
  })
}

const resolveKmzAltitudeContextEditBinding = function (rawPath) {
  const path = String(rawPath || '').trim()
  const lowerPath = path.toLowerCase()
  let scopeId = ''
  let normalizedPath = path
  if (lowerPath.indexOf('template.') === 0) {
    scopeId = 'template'
    normalizedPath = path.slice(9)
  } else if (lowerPath.indexOf('waylines.') === 0) {
    scopeId = 'waylines'
    normalizedPath = path.slice(9)
  }
  return {
    scopeId: scopeId,
    normalizedPath: String(normalizedPath || '').trim()
  }
}

const applyKmzAltitudeContextXmlEdit = function (ctx, point, path, value) {
  const missionState = (ctx && ctx.importMissionState) || buildFallbackMissionState(point ? [point] : [])
  if (!ctx || ctx.importMissionType !== 'kmz' || !missionState) return false
  const binding = resolveKmzAltitudeContextEditBinding(path)
  const fieldKey = resolveKmzAltitudeContextFieldKey(binding.normalizedPath)
  if (!fieldKey) return false
  const targets = []
  let targetPath = ''
  if (fieldKey === 'globalHeight') {
    targetPath = 'wpml:globalHeight'
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push(missionState.folderEl)
    }
    if (binding.scopeId === 'waylines' && missionState.secondaryFolderEl) {
      targets.push(missionState.secondaryFolderEl)
    }
  } else if (fieldKey === 'heightMode') {
    targetPath = 'wpml:waylineCoordinateSysParam.wpml:heightMode'
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (missionState.folderEl) targets.push(missionState.folderEl)
    }
    if (binding.scopeId === 'waylines' && missionState.secondaryFolderEl) {
      targets.push(missionState.secondaryFolderEl)
    }
  } else if (fieldKey === 'executeHeightMode') {
    targetPath = 'wpml:executeHeightMode'
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      if (missionState.secondaryFolderEl) targets.push(missionState.secondaryFolderEl)
    }
    if (binding.scopeId === 'template' && missionState.folderEl) {
      targets.push(missionState.folderEl)
    }
  } else if (fieldKey === 'useGlobalHeight') {
    targetPath = 'wpml:useGlobalHeight'
    if (!binding.scopeId || binding.scopeId === 'template') {
      if (point && point.xmlEl) targets.push(point.xmlEl)
    }
    if (!binding.scopeId || binding.scopeId === 'waylines') {
      const waylinesNode = point && Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
      if (waylinesNode) targets.push(waylinesNode)
    }
  } else {
    return false
  }
  let changed = false
  targets.forEach((targetNode) => {
    changed = updateXmlByPath(targetNode, targetPath, value, function () {}) || changed
  })
  return changed
}

const refreshWaypointMarkerSafe = function (point, ctx, fallbackMissionState) {
  if (!point) return
  if (ctx && typeof ctx.fnRefreshWaypointMarker === 'function') {
    ctx.fnRefreshWaypointMarker(point)
    return
  }
  refreshWaypointMarker(
    point,
    fallbackMissionState || buildFallbackMissionState([point]),
    getMarkerStyle,
    setMarkerStyle
  )
}

const scheduleRoute3dSharedSyncSafe = function (ctx) {
  if (ctx && typeof ctx.fnScheduleRoute3dSharedSync === 'function') {
    ctx.fnScheduleRoute3dSharedSync()
    return
  }
  if (typeof window !== 'undefined' && typeof window.PORTAL_SCHEDULE_ROUTE3D_SHARED_SYNC === 'function') {
    window.PORTAL_SCHEDULE_ROUTE3D_SHARED_SYNC()
  }
}

const syncRoute3dSharedStateSafe = async function (ctx) {
  if (ctx && typeof ctx.fnSyncRoute3dSharedState === 'function') {
    return ctx.fnSyncRoute3dSharedState()
  }
  if (typeof window !== 'undefined' && typeof window.PORTAL_SYNC_ROUTE3D_SHARED_STATE === 'function') {
    return window.PORTAL_SYNC_ROUTE3D_SHARED_STATE()
  }
  scheduleRoute3dSharedSyncSafe(ctx)
  return false
}

const beginRoute3dSharedSyncSuspend = function () {
  if (typeof window === 'undefined' || !window || typeof window !== 'object') return 0
  const currentCount = Number(window.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT)
  const nextCount = (Number.isFinite(currentCount) && currentCount > 0) ? (currentCount + 1) : 1
  window.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT = nextCount
  return nextCount
}

const endRoute3dSharedSyncSuspend = function () {
  if (typeof window === 'undefined' || !window || typeof window !== 'object') return 0
  const currentCount = Number(window.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT)
  const nextCount = (Number.isFinite(currentCount) ? currentCount : 0) - 1
  if (nextCount > 0) {
    window.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT = nextCount
    return nextCount
  }
  try {
    delete window.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT
  } catch (_error) {
    window.PORTAL_ROUTE3D_SYNC_SUSPEND_COUNT = 0
  }
  return 0
}

const buildFallbackMissionState = function (points) {
  const list = Array.isArray(points) ? points.filter((pt) => !!pt) : []
  const firstPoint = list[0] || null
  const templatePlacemark = firstPoint && firstPoint.xmlEl ? firstPoint.xmlEl : null
  const templateDoc = templatePlacemark ? templatePlacemark.ownerDocument : null
  const templateFolderEl = templatePlacemark && templatePlacemark.parentElement ? templatePlacemark.parentElement : null
  const linkedXmlEls = firstPoint && Array.isArray(firstPoint.linkedXmlEls) ? firstPoint.linkedXmlEls.filter((node) => !!node) : []
  const firstLinkedPlacemark = linkedXmlEls[0] || null
  const waylinesDoc = firstLinkedPlacemark ? firstLinkedPlacemark.ownerDocument : null
  const waylinesFolderEl = firstLinkedPlacemark && firstLinkedPlacemark.parentElement ? firstLinkedPlacemark.parentElement : null
  return {
    waypoints: list,
    xmlDoc: templateDoc,
    documentEl: templateDoc ? (templateDoc.getElementsByTagName('Document')[0] || templateDoc.documentElement) : null,
    folderEl: templateFolderEl,
    secondaryXmlDoc: waylinesDoc,
    secondaryDocumentEl: waylinesDoc ? (waylinesDoc.getElementsByTagName('Document')[0] || waylinesDoc.documentElement) : null,
    secondaryFolderEl: waylinesFolderEl
  }
}

const buildAltitudeSyncContext = function (ctx, points) {
  const fallbackMissionState = buildFallbackMissionState(points)
  return {
    importMissionState: ctx.importMissionState || fallbackMissionState,
    DEM_PROVIDER_PRIORITY: Array.isArray(ctx.DEM_PROVIDER_PRIORITY) ? ctx.DEM_PROVIDER_PRIORITY : [],
    ALTITUDE_RULES: (ctx.ALTITUDE_RULES && typeof ctx.ALTITUDE_RULES === 'object')
      ? ctx.ALTITUDE_RULES
      : PORTAL_PRESETS.altitudeRules,
    POINT_HEIGHT_PATH_ALIASES: ctx.POINT_HEIGHT_PATH_ALIASES,
    POINT_EXECUTE_HEIGHT_PATH_ALIASES: ctx.POINT_EXECUTE_HEIGHT_PATH_ALIASES,
    POINT_ELLIPSOID_HEIGHT_PATH_ALIASES: ctx.POINT_ELLIPSOID_HEIGHT_PATH_ALIASES,
    NON_EXPORT_POINT_HEIGHT_PATH: ctx.NON_EXPORT_POINT_HEIGHT_PATH,
    fnIsDemProviderEnabled: (typeof ctx.fnIsDemProviderEnabled === 'function')
      ? ctx.fnIsDemProviderEnabled
      : function () { return true },
    fnSetByPath: (typeof ctx.fnSetByPath === 'function')
      ? ctx.fnSetByPath
      : setByPath,
    fnUpdatePointXmlByPath: (typeof ctx.fnUpdatePointXmlByPath === 'function')
      ? ctx.fnUpdatePointXmlByPath
      : function (point, path, value) {
          return updatePointXmlByPath(point, path, value, function (xmlNode, xmlPath, xmlValue) {
            return updateXmlByPath(xmlNode, xmlPath, xmlValue, function () {})
          })
        },
    fnTemplateSupportsPointCoordinatesField: ctx.fnTemplateSupportsPointCoordinatesField,
    fnBuildPointCoordinatesText: ctx.fnBuildPointCoordinatesText
  }
}

const isWaypointHeightPathSafe = function (path, ctx, points) {
  if (typeof ctx.fnIsWaypointHeightPath === 'function') {
    return ctx.fnIsWaypointHeightPath(path)
  }
  return isWaypointHeightPath(path, buildAltitudeSyncContext(ctx, points))
}

const isEllipsoidHeightPathSafe = function (path, ctx) {
  if (typeof ctx.fnIsEllipsoidHeightPath === 'function') {
    return ctx.fnIsEllipsoidHeightPath(path)
  }
  return isEllipsoidHeightPath(path)
}

const getMissionAltitudeContextSafe = function (ctx, points) {
  if (typeof ctx.fnGetMissionAltitudeContextFor3d === 'function') {
    return ctx.fnGetMissionAltitudeContextFor3d(ctx.importMissionState)
  }
  const altitudeSyncContext = buildAltitudeSyncContext(ctx, points)
  return getMissionAltitudeContextFor3d(altitudeSyncContext.importMissionState, altitudeSyncContext)
}

const isAltitudeEditorPath = function (path, ctx, points) {
  const pathLower = String(path || '').toLowerCase()
  return pathLower === 'coordinates.alt' ||
    pathLower.includes('executeheight') ||
    pathLower.includes('ellipsoidheight') ||
    isWaypointHeightPathSafe(pathLower, ctx, points)
}

const getPropEditorInputValue = function ($input) {
  if (!$input || !$input.length) return ''
  const type = $input.data('prop-type')
  let val = $input.val()
  if (type === 'number') {
    const num = parseFloat(val)
    val = Number.isNaN(num) ? val : num
  } else if (type === 'boolean') {
    val = (String(val).toLowerCase() === 'true')
  }
  return val
}

const getVisibleAltitudeInputValue = function (point, path, ctx) {
  if (!point || !point.props) return undefined
  const pathLower = String(path || '').toLowerCase()
  let nextValue = ctx._.get(point.props, path)
  if ((nextValue === undefined || nextValue === null || nextValue === '') && pathLower === 'coordinates.alt') {
    nextValue = point.altitude
  }
  return nextValue
}

const syncPropClearToggleButtonState = function ($input, $) {
  if (!$input || !$input.length || typeof $ !== 'function') return
  const active = $input.data('propClearOnSave') === true
  const $btn = $input.closest('[class~="input-group"]').find('[data-prop-clear-toggle]').last()
  if (!$btn.length) return
  $btn.toggleClass('active', active)
  $btn.toggleClass('btn-danger', active)
  $btn.toggleClass('text-white', active)
  $btn.toggleClass('btn-outline-danger', !active)
  $btn.attr('aria-pressed', active ? 'true' : 'false')
}

const syncVisibleAltitudeInputs = function ($root, point, ctx, points) {
  if (!$root || !$root.length || !point || !point.props) return
  $root.find('input[data-prop-path]').each(function () {
    const $input = ctx.$(this)
    const path = String($input.data('prop-path') || '')
    if (!isAltitudeEditorPath(path, ctx, points)) return
    const nextValue = getVisibleAltitudeInputValue(point, path, ctx)
    if (nextValue === undefined || nextValue === null) return
    $input.val(String(nextValue))
  })
}

const buildBulkAltitudePreviewPoint = function ($inputs, points, ctx) {
  const firstPoint = Array.isArray(points) ? points.find((pt) => !!pt) : null
  if (!firstPoint) return null
  const previewPoint = {
    props: ctx._.cloneDeep(firstPoint.props || {}),
    latlng: Array.isArray(firstPoint.latlng) ? firstPoint.latlng.slice() : [],
    altitude: firstPoint.altitude
  }
  const fnSetByPathSafe = (typeof ctx.fnSetByPath === 'function') ? ctx.fnSetByPath : setByPath
  $inputs.each(function () {
    const $input = ctx.$(this)
    const forceClear = $input.data('propClearOnSave') === true
    if (!forceClear && $input.data('dirty') !== true) return
    const path = String($input.data('prop-path') || '')
    const pathLower = path.toLowerCase()
    const isPreviewRelevant = pathLower === 'coordinates.lat' ||
      pathLower === 'coordinates.lng' ||
      isAltitudeEditorPath(pathLower, ctx, points) ||
      pathLower === String(ctx.NON_EXPORT_POINT_HEIGHT_PATH || '').toLowerCase()
    if (!isPreviewRelevant) return
    const val = forceClear
      ? ((pathLower === String(ctx.NON_EXPORT_POINT_HEIGHT_PATH || '').toLowerCase()) ? null : '')
      : getPropEditorInputValue($input)
    fnSetByPathSafe(previewPoint.props, path, val)
  })
  const lat = parseFloat(ctx._.get(previewPoint.props, 'coordinates.lat'))
  const lng = parseFloat(ctx._.get(previewPoint.props, 'coordinates.lng'))
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    previewPoint.latlng = [lat, lng]
  }
  const alt = ctx.toFiniteNumber(ctx._.get(previewPoint.props, 'coordinates.alt'))
  if (alt !== null) {
    previewPoint.altitude = alt
  }
  return previewPoint
}

const syncBulkPreviewAltitudeInputs = function ($root, previewPoint, sourcePath, ctx) {
  if (!$root || !$root.length || !previewPoint || !previewPoint.props) return
  const sourcePathLower = String(sourcePath || '').toLowerCase()
  $root.find('input[data-prop-path]').each(function () {
    const $input = ctx.$(this)
    const path = String($input.data('prop-path') || '')
    const pathLower = path.toLowerCase()
    if (!isAltitudeEditorPath(pathLower, ctx, [previewPoint])) return
    if (pathLower === sourcePathLower) return
    const nextValue = getVisibleAltitudeInputValue(previewPoint, path, ctx)
    const displayValue = (nextValue === undefined || nextValue === null) ? '' : String(nextValue)
    $input.val(displayValue)
    $input.data('dirty', false)
    if ($input.data('propClearOnSave') === true) {
      $input.data('propClearOnSave', false)
      syncPropClearToggleButtonState($input, ctx.$)
    }
  })
}

const runBulkAltitudePreviewSync = function ($root, $inputs, $sourceInput, points, ctx) {
  if (!$sourceInput || !$sourceInput.length) return
  const sourcePath = String($sourceInput.data('prop-path') || '')
  const sourcePathLower = sourcePath.toLowerCase()
  if (!isAltitudeEditorPath(sourcePathLower, ctx, points)) return
  if ($sourceInput.data('propClearOnSave') === true) {
    $sourceInput.data('propClearOnSave', false)
    syncPropClearToggleButtonState($sourceInput, ctx.$)
  }
  const previewPoint = buildBulkAltitudePreviewPoint($inputs, points, ctx)
  if (!previewPoint) return
  const altitudeSyncContext = buildAltitudeSyncContext(ctx, [previewPoint])
  altitudeSyncContext.fnUpdatePointXmlByPath = function () {}
  const liveContext = getMissionAltitudeContextSafe(ctx, points)
  const sourceValue = getPropEditorInputValue($sourceInput)
  const syncResult = syncPointAltitudeFields(previewPoint, {
    sourceMissionAltContext: liveContext,
    targetMissionAltContext: liveContext,
    absoluteAltitudeOverride: (sourcePathLower === 'coordinates.alt' || isEllipsoidHeightPathSafe(sourcePathLower, ctx))
      ? ctx.toFiniteNumber(sourceValue)
      : null,
    sourceTemplateHeightOverride: isWaypointHeightPathSafe(sourcePathLower, ctx, [previewPoint])
      ? ctx.toFiniteNumber(sourceValue)
      : null,
    sourceExecuteHeightOverride: (sourcePathLower.includes('executeheight') && !isWaypointHeightPathSafe(sourcePathLower, ctx, [previewPoint]))
      ? ctx.toFiniteNumber(sourceValue)
      : null
  }, altitudeSyncContext)
  if (syncResult && syncResult.absoluteAltitude !== null) {
    previewPoint.altitude = syncResult.absoluteAltitude
  }
  syncBulkPreviewAltitudeInputs($root, previewPoint, sourcePath, ctx)
}

const renderPortalPropEditorRows = function ($container, rows, options, ctx) {
  const {
    _,
    $,
    fnT,
    NON_EXPORT_PROJECT_FILENAME_PATH,
    NON_EXPORT_POINT_HEIGHT_PATH,
    DEM_PROVIDER_PRIORITY,
    fnGetDemProviderLabel,
    fnRunPointHeightDemOverwriteFlow,
    fnGetPointHeightToolFieldMeta,
    fnGetPointNumericByPathAliases,
    toFiniteNumber,
    pathWithoutNamespacePrefixes,
    pathWithoutNumericParts,
    fnResolvePropPresetOptions,
    getPathLeafField,
    pathWithoutLeadingScope,
    fnResolvePropLabelTooltip,
    fnResolvePropLabelDetailedHelp,
    fnResolvePropLabelIconClass,
    fnResolvePropLabelRequired,
    fnBuildHeadingSuggestModel,
    toUnixMs,
    fnFormatUnixMsForDisplay,
    fnParseDisplayTimestampToUnixMs,
    TIMESTAMP_DISPLAY_FORMAT,
    fnResolveI18Text,
    fnResolvePropValueHelp,
    fnShowPropContextMenu,
    PROP_FIELD_DISABLE_RULES
  } = ctx
  return renderPropEditorRows($container, rows, options, {
    _,
    $,
    fnT,
    NON_EXPORT_PROJECT_FILENAME_PATH,
    NON_EXPORT_POINT_HEIGHT_PATH,
    DEM_PROVIDER_PRIORITY,
    fnGetDemProviderLabel,
    fnRunPointHeightDemOverwriteFlow,
    fnGetPointHeightToolFieldMeta,
    fnGetPointNumericByPathAliases,
    toFiniteNumber,
    pathWithoutNamespacePrefixes,
    pathWithoutNumericParts,
    fnResolvePropPresetOptions,
    getPathLeafField,
    pathWithoutLeadingScope,
    fnResolvePropLabelTooltip,
    fnResolvePropLabelDetailedHelp,
    fnResolvePropLabelIconClass,
    fnResolvePropLabelRequired,
    fnBuildHeadingSuggestModel,
    toUnixMs,
    fnFormatUnixMsForDisplay,
    fnParseDisplayTimestampToUnixMs,
    TIMESTAMP_DISPLAY_FORMAT,
    fnResolveI18Text,
    fnResolvePropValueHelp,
    fnShowPropContextMenu,
    PROP_FIELD_DISABLE_RULES
  })
}

const openPointPropsSwal = function (title, point, onCoordsChange, onPropsChange, ctx) {
  const props = (point && point.props) ? point.props : {}
  const historyBefore = ctx.fnCaptureState()
  let dirty = false
  let singleAxisLayer = null
  const fnRemoveSingleAxisLayer = function () {
    if (singleAxisLayer && ctx.drawnItems && ctx.drawnItems.hasLayer(singleAxisLayer)) {
      ctx.drawnItems.removeLayer(singleAxisLayer)
    }
    singleAxisLayer = null
  }
  ctx.areaSwal.fire({
    position: 'bottom-right',
    title: title,
    size: 'lg',
    showCancelButton: true,
    confirmButtonText: ctx.fnT('common.ok'),
    cancelButtonText: ctx.fnT('common.cancel'),
    willClose: function () {
      fnRemoveSingleAxisLayer()
      if (dirty) {
        ctx.fnHistoryPush(historyBefore)
      }
    },
    didClose: function () {
      fnRemoveSingleAxisLayer()
    },
    didOpen: function (modal) {
      const $cont = ctx.$(modal).find('.swal2-html-container').addClass('text-start')
      const $footer = ctx.$(modal).find('.modal-footer').first()
      $cont.empty()
      const lat = parseFloat(ctx._.get(props, 'coordinates.lat'))
      const lng = parseFloat(ctx._.get(props, 'coordinates.lng'))
      singleAxisLayer = ctx.fnAttachSeekAxisLayerToModal(modal, { lat, lng })
      const templateDefaults = applyPortalPresetDefaultsToFlatMap(ctx.fnGetTemplateWaypointDefaults())
      const shouldPersistPointCoordinates = ctx.fnTemplateSupportsPointCoordinatesField()
      const rowMap = ctx._.assign({}, templateDefaults, ctx.fnPropsToMap(props))
      if (ctx.importMissionType === 'kmz') {
        applyKmzResolvedHeadingDisplayValuesToMap(rowMap, point, 'playback')
        applyKmzResolvedSpeedDisplayValuesToMap(rowMap, point, 'playback')
        applyKmzResolvedGimbalDisplayValuesToMap(rowMap, point, 'playback')
        applyKmzResolvedPayloadDisplayValuesToMap(rowMap, point, 'playback')
        applyKmzResolvedTurnDisplayValuesToMap(rowMap, point, 'playback')
        applyKmzResolvedStraightLineDisplayValuesToMap(rowMap, point, 'playback')
        applyKmzResolvedAltitudeContextDisplayValuesToMap(rowMap, point, 'playback')
      }
      if (shouldPersistPointCoordinates && props && props.coordinates && props.coordinates.lat !== undefined && props.coordinates.lng !== undefined) {
        rowMap['Point.coordinates'] = ctx.fnBuildPointCoordinatesText(props)
      }
      if (ctx.importMissionType !== 'kmz' && !Object.prototype.hasOwnProperty.call(rowMap, 'Point.altitudeMode')) {
        rowMap['Point.altitudeMode'] = ''
      }
      if (!Object.prototype.hasOwnProperty.call(rowMap, INTERNAL_POINT_NAME_PATH)) {
        rowMap[INTERNAL_POINT_NAME_PATH] = ctx.fnGetPointName(point) || ''
      }
      if (!Object.prototype.hasOwnProperty.call(rowMap, ctx.NON_EXPORT_POINT_HEIGHT_PATH)) {
        rowMap[ctx.NON_EXPORT_POINT_HEIGHT_PATH] = ''
      }
      let rows = ctx.fnSortPointEditorRows(ctx.fnPropsMapToRows(ctx.fnFilterPointEditorMapForMissionType(rowMap)))
      if (ctx.importMissionType !== 'kmz') {
        rows = rows.filter((row) => !ctx.fnIsKmlActionPath(row && row.path))
      } else {
        rows = rows.filter((row) => !ctx.fnIsKmzActionPath(row && row.path))
      }
      if (!rows.length) {
        $cont.text(ctx.fnT('ui.no_displayable_data'))
        return
      }
      const $inputs = renderPortalPropEditorRows($cont, rows, {
        idPrefix: 'single-edit',
        markDirty: false,
        readonlyPaths: ['wpml:index', 'index'],
        templateDefaults: templateDefaults,
        compactSharedPrefix: ctx.importMissionType === 'kmz',
        stripNamespacePrefixes: ctx.importMissionType === 'kmz',
        kmzScopeContext: ctx.importMissionType === 'kmz' ? 'waypoint' : '',
        pointTargets: point ? [point] : [],
        onPointHeightToolApplied: function () {
          dirty = true
          if (typeof onPropsChange === 'function') {
            onPropsChange({ path: ctx.NON_EXPORT_POINT_HEIGHT_PATH, value: null, props: props })
          }
        }
      }, ctx)
      $inputs.on('input', function () {
        const $input = ctx.$(this)
        const path = $input.data('prop-path')
        const type = $input.data('prop-type')
        let val = $input.val()
        if (type === 'number') {
          const num = parseFloat(val)
          val = Number.isNaN(num) ? val : num
        } else if (type === 'boolean') {
          val = (String(val).toLowerCase() === 'true')
        }
        ctx.fnSetByPath(props, path, val)
        const pathLower = String(path || '').toLowerCase()
        const isProjectPath = pathLower.indexOf('project.') === 0
        const isKmzHeadingField = ctx.importMissionType === 'kmz' && isKmzHeadingFieldPath(pathLower)
        const isKmzGlobalHeadingField = ctx.importMissionType === 'kmz' && isKmzGlobalHeadingFieldPath(pathLower)
        const isKmzSpeedField = ctx.importMissionType === 'kmz' && isKmzSpeedFieldPath(pathLower)
        const isKmzGlobalSpeedField = ctx.importMissionType === 'kmz' && isKmzGlobalSpeedFieldPath(pathLower)
        const isKmzGimbalField = ctx.importMissionType === 'kmz' && isKmzGimbalFieldPath(pathLower)
        const isKmzGlobalGimbalField = ctx.importMissionType === 'kmz' && isKmzGlobalGimbalFieldPath(pathLower)
        const isKmzPayloadField = ctx.importMissionType === 'kmz' && isKmzPayloadFieldPath(pathLower)
        const isKmzGlobalPayloadField = ctx.importMissionType === 'kmz' && isKmzGlobalPayloadFieldPath(pathLower)
        const isKmzTurnField = ctx.importMissionType === 'kmz' && isKmzTurnFieldPath(pathLower)
        const isKmzGlobalTurnField = ctx.importMissionType === 'kmz' && isKmzGlobalTurnFieldPath(pathLower)
        const isKmzStraightLineField = ctx.importMissionType === 'kmz' && isKmzStraightLineFieldPath(pathLower)
        const isKmzGlobalStraightLineField = ctx.importMissionType === 'kmz' && isKmzGlobalStraightLineFieldPath(pathLower)
        const isKmzAltitudeContextField = ctx.importMissionType === 'kmz' && isKmzAltitudeContextFieldPath(pathLower)
        const isKmzGlobalAltitudeContextField = ctx.importMissionType === 'kmz' && isKmzGlobalAltitudeContextFieldPath(pathLower)
        if (point && pathLower !== ctx.NON_EXPORT_POINT_HEIGHT_PATH && !isProjectPath) {
          if (isKmzHeadingField) {
            applyKmzHeadingXmlEdit(ctx, point, path, val)
          } else if (isKmzSpeedField) {
            applyKmzSpeedXmlEdit(ctx, point, path, val)
          } else if (isKmzGimbalField) {
            applyKmzGimbalXmlEdit(ctx, point, path, val)
          } else if (isKmzPayloadField) {
            applyKmzPayloadXmlEdit(ctx, point, path, val)
          } else if (isKmzTurnField) {
            applyKmzTurnXmlEdit(ctx, point, path, val)
          } else if (isKmzStraightLineField) {
            applyKmzStraightLineXmlEdit(ctx, point, path, val)
          } else if (isKmzAltitudeContextField) {
            applyKmzAltitudeContextXmlEdit(ctx, point, path, val)
          } else {
            ctx.fnUpdatePointXmlByPath(point, path, val)
          }
        }
        if (point && isKmzHeadingField) {
          syncKmzHeadingScopesForPoints(
            isKmzGlobalHeadingField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point],
            ctx,
            { global: isKmzGlobalHeadingField }
          )
          if (typeof ctx.fnRefreshWaypointMarker === 'function') {
            const refreshTargets = isKmzGlobalHeadingField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point]
            refreshTargets.forEach((pt) => {
              if (pt) ctx.fnRefreshWaypointMarker(pt)
            })
          }
        }
        if (point && isKmzSpeedField) {
          syncKmzSpeedScopesForPoints(
            isKmzGlobalSpeedField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point],
            ctx,
            { global: isKmzGlobalSpeedField }
          )
        }
        if (point && isKmzGimbalField) {
          syncKmzGimbalScopesForPoints(
            isKmzGlobalGimbalField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point],
            ctx,
            { global: isKmzGlobalGimbalField }
          )
        }
        if (point && isKmzPayloadField) {
          syncKmzPayloadScopesForPoints(
            isKmzGlobalPayloadField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point],
            ctx,
            { global: isKmzGlobalPayloadField }
          )
        }
        if (point && isKmzTurnField) {
          syncKmzTurnScopesForPoints(
            isKmzGlobalTurnField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point],
            ctx,
            { global: isKmzGlobalTurnField }
          )
        }
        if (point && isKmzStraightLineField) {
          syncKmzStraightLineScopesForPoints(
            isKmzGlobalStraightLineField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
              ? ctx.importMissionState.waypoints
              : [point],
            ctx,
            { global: isKmzGlobalStraightLineField }
          )
        }
        if (point && isKmzAltitudeContextField) {
          const altitudeContextTargets = isKmzGlobalAltitudeContextField && ctx.importMissionState && Array.isArray(ctx.importMissionState.waypoints)
            ? ctx.importMissionState.waypoints
            : [point]
          syncKmzAltitudeScopesForPoints(
            altitudeContextTargets,
            ctx,
            { global: isKmzGlobalAltitudeContextField }
          )
          altitudeContextTargets.forEach((pt) => {
            refreshWaypointMarkerSafe(pt, ctx, buildFallbackMissionState(altitudeContextTargets))
          })
        }
        if (point && pathLower === INTERNAL_POINT_NAME_PATH) {
          const resolvedPointName = ctx.fnGetPointName(point)
          ctx.fnSyncPointActionFileSuffixWithNameWithContext(point, resolvedPointName)
          syncVisibleKmzActionFileSuffixInputs($cont, resolvedPointName, ctx.$)
        }
        const isAltitudeField = pathLower === 'coordinates.alt' ||
          pathLower.includes('executeheight') ||
          pathLower.includes('ellipsoidheight') ||
          isWaypointHeightPathSafe(pathLower, ctx, point ? [point] : [])
        if (point && isAltitudeField) {
          const altitudeSyncContext = buildAltitudeSyncContext(ctx, point ? [point] : [])
          const liveContext = getMissionAltitudeContextSafe(ctx, point ? [point] : [])
          const explicitAbsoluteAltitude = (pathLower === 'coordinates.alt' || isEllipsoidHeightPathSafe(pathLower, ctx))
            ? ctx.toFiniteNumber(val)
            : null
          const explicitTemplateHeight = isWaypointHeightPathSafe(pathLower, ctx, point ? [point] : [])
            ? ctx.toFiniteNumber(val)
            : null
          const explicitExecuteHeight = (pathLower.includes('executeheight') && !isWaypointHeightPathSafe(pathLower, ctx, point ? [point] : []))
            ? ctx.toFiniteNumber(val)
            : null
          const syncResult = syncPointAltitudeFields(point, {
            sourceMissionAltContext: liveContext,
            targetMissionAltContext: liveContext,
            absoluteAltitudeOverride: explicitAbsoluteAltitude,
            sourceTemplateHeightOverride: explicitTemplateHeight,
            sourceExecuteHeightOverride: explicitExecuteHeight
          }, altitudeSyncContext)
          if (syncResult && syncResult.absoluteAltitude !== null) {
            point.altitude = syncResult.absoluteAltitude
          }
          syncVisibleAltitudeInputs($cont, point, ctx, point ? [point] : [])
        }
        if (path === 'coordinates.lat' || path === 'coordinates.lng') {
          if (typeof onCoordsChange === 'function') {
            const lat = parseFloat(props.coordinates && props.coordinates.lat)
            const lng = parseFloat(props.coordinates && props.coordinates.lng)
            if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
              onCoordsChange({ lat, lng })
            }
          }
        }
        if (path === 'Point.coordinates') {
          const parsedCoord = parseKmlCoordTriplet(val)
          if (parsedCoord) {
            if (!props.coordinates || typeof props.coordinates !== 'object') {
              props.coordinates = {}
            }
            props.coordinates.lat = parsedCoord.lat
            props.coordinates.lng = parsedCoord.lng
            if (parsedCoord.alt === null) {
              delete props.coordinates.alt
            } else {
              props.coordinates.alt = parsedCoord.alt
            }
            if (typeof onCoordsChange === 'function') {
              onCoordsChange({ lat: parsedCoord.lat, lng: parsedCoord.lng })
            } else if (point && Array.isArray(point.latlng)) {
              point.latlng = [parsedCoord.lat, parsedCoord.lng]
              if (point.marker && typeof point.marker.setLatLng === 'function') {
                point.marker.setLatLng(point.latlng)
              }
              ctx.fnUpdatePointCoordinates(point, parsedCoord.lat, parsedCoord.lng)
            }
            if (point && parsedCoord.alt !== null) {
              const altitudeSyncContext = buildAltitudeSyncContext(ctx, point ? [point] : [])
              const liveContext = getMissionAltitudeContextSafe(ctx, point ? [point] : [])
              const syncResult = syncPointAltitudeFields(point, {
                sourceMissionAltContext: liveContext,
                targetMissionAltContext: liveContext,
                absoluteAltitudeOverride: parsedCoord.alt
              }, altitudeSyncContext)
              if (syncResult && syncResult.absoluteAltitude !== null) {
                point.altitude = syncResult.absoluteAltitude
              }
              syncVisibleAltitudeInputs($cont, point, ctx, point ? [point] : [])
            }
          }
        }
        if (shouldPersistPointCoordinates && (path === 'coordinates.lat' || path === 'coordinates.lng' || path === 'coordinates.alt')) {
          const pointCoordinatesText = ctx.fnBuildPointCoordinatesText(props)
          ctx.fnSetByPath(props, 'Point.coordinates', pointCoordinatesText)
          if (point) ctx.fnUpdatePointXmlByPath(point, 'Point.coordinates', pointCoordinatesText)
          $cont.find('input').each(function () {
            const $rowInput = ctx.$(this)
            if ($rowInput.data('prop-path') === 'Point.coordinates') {
              $rowInput.val(pointCoordinatesText)
            }
          })
        }
        if (typeof onPropsChange === 'function') {
          onPropsChange({ path: path, value: val, props: props })
        }
        if (point && pathLower !== ctx.NON_EXPORT_POINT_HEIGHT_PATH && !isProjectPath) {
          scheduleRoute3dSharedSyncSafe(ctx)
        }
        dirty = true
      })
      if (ctx.importMissionType !== 'kmz') {
        const $actionsCont = ctx.$('<div class="mt-2"></div>')
        $cont.append($actionsCont)
        const initialActions = ctx.fnGetKmlActionsFromProps(props)
        renderKmlActionsEditor($actionsCont, initialActions, function (nextActions) {
          ctx.fnSetKmlActionsInProps(props, nextActions)
          if (point) ctx.fnSetPointKmlActionsWithContext(point, nextActions)
          if (typeof onPropsChange === 'function') {
            onPropsChange({ path: 'ExtendedData.mis:actions', value: nextActions, props: props })
          }
          scheduleRoute3dSharedSyncSafe(ctx)
          dirty = true
        })
      } else {
        const $actionsCont = ctx.$('<div class="mt-2"></div>')
        $cont.append($actionsCont)
        const initialGroups = ctx.fnApplyKmzActionFileSuffixDisplayModeToGroups(ctx.fnGetKmzActionGroupsFromProps(props, point), 'pointName', function () {
          return ctx.fnGetPointName(point)
        })
        renderKmzActionGroupsEditor($actionsCont, initialGroups, function (nextGroups) {
          const resolvedGroups = ctx.fnResolveKmzActionFileSuffixPointNameTokensInGroups(nextGroups, function () {
            return ctx.fnGetPointName(point)
          })
          const actionGroupOptions = {
            alignReachPointRangeToIndex: true,
            alignBetweenAdjacentPointsRangeToIndex: true,
            ensureMultipleTimingTriggerParam: true
          }
          const normalizedGroups = ctx.fnSetKmzActionGroupsInProps(props, point, resolvedGroups, actionGroupOptions)
          if (point) ctx.fnSetPointKmzActionGroupsWithContext(point, normalizedGroups, actionGroupOptions)
          if (typeof onPropsChange === 'function') {
            onPropsChange({ path: 'wpml:actionGroup', value: normalizedGroups, props: props })
          }
          scheduleRoute3dSharedSyncSafe(ctx)
          dirty = true
        }, {
          fileSuffixMode: 'pointName',
          pointNameResolver: function () { return ctx.fnGetPointName(point) },
          defaultPointIndex: Number.isFinite(Number(ctx._.get(point, 'idx'))) ? Number(ctx._.get(point, 'idx')) : 0
        })
      }
    }
  })
}

const openBulkEditSwal = function (points, ctx) {
  if (!Array.isArray(points) || !points.length) return
  const historyBefore = ctx.fnCaptureState()
  const merged = ctx.fnMergePropsForEdit(points)
  const fallbackMissionState = ctx.importMissionState || buildFallbackMissionState(points)
  let hasCommittedChanges = false
  let hasCommittedHistoryEntry = false
  let hasPendingActionChanges = false
  let kmlBulkActionsDraft = null
  let kmzBulkGroupsDraft = null
  let bulkAxisLayer = null
  let allowModalClose = false
  let fnApplyPendingBulkChanges = async function () { return false }
  const fnCommitBulkHistoryIfNeeded = function () {
    if (!hasCommittedChanges || hasCommittedHistoryEntry) return
    if (typeof ctx.fnHistoryPush === 'function') {
      ctx.fnHistoryPush(historyBefore)
    }
    hasCommittedHistoryEntry = true
  }
  ctx.areaSwal.fire({
    position: 'bottom-right',
    title: ctx.fnTF('bulk.title', { count: points.length }),
    width: 900,
    customClass: { popup: 'swal-bulk-edit' },
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: ctx.fnT('bulk.apply'),
    denyButtonText: ctx.fnT('common.save'),
    cancelButtonText: ctx.fnT('common.cancel'),
    allowEscapeKey: false,
    willClose: function () {
      if (!allowModalClose) return
      if (bulkAxisLayer && ctx.drawnItems && ctx.drawnItems.hasLayer(bulkAxisLayer)) {
        ctx.drawnItems.removeLayer(bulkAxisLayer)
        bulkAxisLayer = null
      }
      if (hasCommittedChanges && !hasCommittedHistoryEntry) {
        ctx.fnHistoryPush(historyBefore)
      }
    },
    didClose: function () {
      if (bulkAxisLayer && ctx.drawnItems && ctx.drawnItems.hasLayer(bulkAxisLayer)) {
        ctx.drawnItems.removeLayer(bulkAxisLayer)
        bulkAxisLayer = null
      }
    },
    didOpen: function (modal) {
      const $popup = ctx.$(modal)
      const $cont = $popup.find('.swal2-html-container').addClass('text-start')
      $cont.empty()
      let anchorLatLng = null
      const firstPoint = Array.isArray(points) ? points.find(pt => pt && Array.isArray(pt.latlng)) : null
      if (firstPoint && firstPoint.latlng.length >= 2) {
        const lat = parseFloat(firstPoint.latlng[0])
        const lng = parseFloat(firstPoint.latlng[1])
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          anchorLatLng = { lat, lng }
        }
      }
      bulkAxisLayer = ctx.fnAttachSeekAxisLayerToModal(modal, anchorLatLng)
      const templateDefaults = applyPortalPresetDefaultsToFlatMap(ctx.fnGetTemplateWaypointDefaults())
      let rows = ctx.fnSortPointEditorRows(ctx.fnPropsMapToRows(ctx.fnFilterPointEditorMapForMissionType(merged)))
      if (ctx.importMissionType !== 'kmz') {
        rows = rows.filter((row) => !ctx.fnIsKmlActionPath(row && row.path))
      } else {
        rows = rows.filter((row) => !ctx.fnIsKmzActionPath(row && row.path))
      }
      const $inputs = renderPortalPropEditorRows($cont, rows, {
        idPrefix: 'bulk-edit',
        markDirty: true,
        readonlyPaths: ['wpml:index', 'index'],
        templateDefaults: templateDefaults,
        compactSharedPrefix: ctx.importMissionType === 'kmz',
        stripNamespacePrefixes: ctx.importMissionType === 'kmz',
        kmzScopeContext: ctx.importMissionType === 'kmz' ? 'waypoint' : '',
        enableClearToggle: true,
        pointTargets: points,
        onPointHeightToolApplied: function () {
          hasCommittedChanges = true
          fnCommitBulkHistoryIfNeeded()
          fnRenderProgress(points.length, points.length, false, 'bulk.progress_done')
        }
      }, ctx)
      let bulkLoadingTask = null

      const fnYieldToUi = function () {
        return new Promise((resolve) => {
          if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(function () {
              resolve()
            })
            return
          }
          setTimeout(resolve, 0)
        })
      }

      const fnSetBulkUiBusy = function (active) {
        $popup.find('input, select, textarea, button').prop('disabled', !!active)
      }

      const fnRenderProgress = function (current, total, isActive, textKey) {
        const safeTotal = Math.max(0, Number(total) || 0)
        const safeCurrent = Math.max(0, Math.min(safeTotal, Number(current) || 0))
        const percent = safeTotal > 0 ? Math.max(0, Math.min(100, Math.round((safeCurrent / safeTotal) * 100))) : 0
        const progressLabel = textKey
          ? ctx.fnTF(textKey, {
              current: safeCurrent,
              total: safeTotal,
              percent: percent
            })
          : ''
        if (!bulkLoadingTask && (isActive || safeTotal > 0)) {
          bulkLoadingTask = startPortalLoadingTask({
            title: ctx.fnTF('bulk.title', { count: points.length }),
            status: progressLabel,
            progress: percent,
            mode: 'determinate'
          })
        }
        if (!bulkLoadingTask) return
        if (!isActive && safeTotal === 0) {
          bulkLoadingTask.remove()
          bulkLoadingTask = null
          return
        }
        if (isActive) {
          bulkLoadingTask.update({
            status: progressLabel,
            progress: percent,
            mode: 'determinate'
          })
          return
        }
        bulkLoadingTask.complete({
          status: progressLabel,
          progress: percent
        })
        bulkLoadingTask = null
      }

      const fnSyncAltitudeAfterBulkEdits = function (targetPoints, editedMap) {
        const editedPaths = Object.keys(editedMap || {})
        const altitudePaths = editedPaths.filter((path) => {
          const pathLower = String(path || '').toLowerCase()
          return pathLower === 'coordinates.alt' ||
            pathLower.includes('executeheight') ||
            pathLower.includes('ellipsoidheight') ||
            isWaypointHeightPathSafe(pathLower, ctx, targetPoints)
        })
        if (!altitudePaths.length) return
        const altitudeSyncContext = buildAltitudeSyncContext(ctx, targetPoints)
        const liveContext = getMissionAltitudeContextSafe(ctx, targetPoints)
        const singleAltitudePath = altitudePaths.length === 1 ? String(altitudePaths[0] || '') : ''
        const singleAltitudeLower = singleAltitudePath.toLowerCase()
        const singleValue = singleAltitudePath ? ctx.toFiniteNumber(editedMap[singleAltitudePath]) : null
        ;(Array.isArray(targetPoints) ? targetPoints : []).forEach((pt) => {
          const syncResult = syncPointAltitudeFields(pt, {
            sourceMissionAltContext: liveContext,
            targetMissionAltContext: liveContext,
            absoluteAltitudeOverride: (singleAltitudeLower === 'coordinates.alt' || isEllipsoidHeightPathSafe(singleAltitudeLower, ctx)) ? singleValue : null,
            sourceTemplateHeightOverride: (singleAltitudePath && isWaypointHeightPathSafe(singleAltitudeLower, ctx, targetPoints)) ? singleValue : null,
            sourceExecuteHeightOverride: (singleAltitudeLower.includes('executeheight') && !isWaypointHeightPathSafe(singleAltitudeLower, ctx, targetPoints)) ? singleValue : null
          }, altitudeSyncContext)
          if (!syncResult || (syncResult.changed !== true && syncResult.absoluteAltitude === null)) return
          if (pt.marker && typeof pt.marker.setTooltipContent === 'function' && typeof ctx.fnGetWaypointTooltipText === 'function') {
            pt.marker.setTooltipContent(ctx.fnGetWaypointTooltipText(pt))
          }
          refreshWaypointMarkerSafe(pt, ctx, fallbackMissionState)
        })
      }

      const fnCollectDirtyEdits = function () {
        const edits = {}
        const dirtyInputs = []
        $inputs.each(function () {
          const $input = ctx.$(this)
          const forceClear = $input.data('propClearOnSave') === true
          if (!forceClear && $input.data('dirty') !== true) return
          const path = $input.data('prop-path')
          const pathLower = String(path || '').toLowerCase()
          const type = $input.data('prop-type')
          let val = forceClear
            ? ((pathLower === ctx.NON_EXPORT_POINT_HEIGHT_PATH) ? null : '')
            : $input.val()
          if (!forceClear) {
            if (type === 'number') {
              const num = parseFloat(val)
              val = Number.isNaN(num) ? val : num
            } else if (type === 'boolean') {
              val = (String(val).toLowerCase() === 'true')
            }
          }
          edits[path] = val
          dirtyInputs.push({
            $input: $input,
            appliedValue: val,
            forceClear: forceClear
          })
        })
        return {
          edits: edits,
          dirtyInputs: dirtyInputs
        }
      }

      const fnClearAppliedDirtyFlags = function (dirtyInputs) {
        ;(Array.isArray(dirtyInputs) ? dirtyInputs : []).forEach((entry) => {
          const $input = entry && entry.$input
          if (!$input || !$input.length) return
          const currentType = $input.data('prop-type')
          let currentValue = $input.val()
          if (currentType === 'number') {
            const num = parseFloat(currentValue)
            currentValue = Number.isNaN(num) ? currentValue : num
          } else if (currentType === 'boolean') {
            currentValue = (String(currentValue).toLowerCase() === 'true')
          }
          const currentComparable = JSON.stringify(currentValue)
          const appliedComparable = JSON.stringify(entry.appliedValue)
          if (currentComparable !== appliedComparable) return
          if (entry.forceClear && $input.data('propClearOnSave') !== true) return
          $input.data('dirty', false)
        })
      }

      let bulkTaskTail = Promise.resolve()

      const fnRunQueuedBulkTask = function (taskFactory) {
        bulkTaskTail = bulkTaskTail
          .catch(function () {})
          .then(async function () {
            fnSetBulkUiBusy(true)
            try {
              await taskFactory()
            } finally {
              fnSetBulkUiBusy(false)
            }
          })
        return bulkTaskTail
      }

      const fnRunPointsWithProgress = async function (taskPoints, taskRunner) {
        const total = Array.isArray(taskPoints) ? taskPoints.length : 0
        if (!total || typeof taskRunner !== 'function') {
          fnRenderProgress(0, 0, false, 'bulk.progress_idle')
          return
        }
        const chunkSize = Math.max(5, Math.min(20, Math.ceil(total / 40)))
        fnRenderProgress(0, total, true, 'bulk.progress_running')
        await fnYieldToUi()
        for (let startIdx = 0; startIdx < total; startIdx += chunkSize) {
          const chunk = taskPoints.slice(startIdx, Math.min(total, startIdx + chunkSize))
          await taskRunner(chunk)
          fnRenderProgress(Math.min(total, startIdx + chunk.length), total, true, 'bulk.progress_running')
          await fnYieldToUi()
        }
        fnRenderProgress(total, total, false, 'bulk.progress_done')
      }

      const fnQueueBulkPropertyApply = function (edits, dirtyInputs) {
        const hasEdits = !!Object.keys(edits || {}).length
        if (!hasEdits) {
          fnRenderProgress(0, 0, false, 'bulk.progress_idle')
          return Promise.resolve()
        }
        const internalEdits = {}
        const exportableEdits = {}
        Object.keys(edits || {}).forEach((path) => {
          const pathLower = String(path || '').toLowerCase()
          if (pathLower.indexOf('project.') === 0) {
            internalEdits[path] = edits[path]
            return
          }
          exportableEdits[path] = edits[path]
        })
        const globalHeadingEdits = {}
        const pointHeadingEdits = {}
        const globalSpeedEdits = {}
        const pointSpeedEdits = {}
        const globalGimbalEdits = {}
        const pointGimbalEdits = {}
        const globalPayloadEdits = {}
        const pointPayloadEdits = {}
        const globalTurnEdits = {}
        const pointTurnEdits = {}
        const globalStraightLineEdits = {}
        const pointStraightLineEdits = {}
        const globalAltitudeContextEdits = {}
        const pointAltitudeContextEdits = {}
        const genericPointEdits = {}
        Object.keys(exportableEdits).forEach((path) => {
          if (ctx.importMissionType === 'kmz' && isKmzGlobalHeadingFieldPath(path)) {
            globalHeadingEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzHeadingFieldPath(path)) {
            pointHeadingEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGlobalSpeedFieldPath(path)) {
            globalSpeedEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzSpeedFieldPath(path)) {
            pointSpeedEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGlobalGimbalFieldPath(path)) {
            globalGimbalEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGimbalFieldPath(path)) {
            pointGimbalEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGlobalPayloadFieldPath(path)) {
            globalPayloadEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzPayloadFieldPath(path)) {
            pointPayloadEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGlobalTurnFieldPath(path)) {
            globalTurnEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzTurnFieldPath(path)) {
            pointTurnEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGlobalStraightLineFieldPath(path)) {
            globalStraightLineEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzStraightLineFieldPath(path)) {
            pointStraightLineEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzGlobalAltitudeContextFieldPath(path)) {
            globalAltitudeContextEdits[path] = exportableEdits[path]
            return
          }
          if (ctx.importMissionType === 'kmz' && isKmzAltitudeContextFieldPath(path)) {
            pointAltitudeContextEdits[path] = exportableEdits[path]
            return
          }
          genericPointEdits[path] = exportableEdits[path]
        })
        const hasHeadingEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalHeadingEdits).length > 0 || Object.keys(pointHeadingEdits).length > 0)
        const hasSpeedEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalSpeedEdits).length > 0 || Object.keys(pointSpeedEdits).length > 0)
        const hasGimbalEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalGimbalEdits).length > 0 || Object.keys(pointGimbalEdits).length > 0)
        const hasPayloadEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalPayloadEdits).length > 0 || Object.keys(pointPayloadEdits).length > 0)
        const hasTurnEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalTurnEdits).length > 0 || Object.keys(pointTurnEdits).length > 0)
        const hasStraightLineEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalStraightLineEdits).length > 0 || Object.keys(pointStraightLineEdits).length > 0)
        const hasAltitudeContextEdits = ctx.importMissionType === 'kmz' &&
          (Object.keys(globalAltitudeContextEdits).length > 0 || Object.keys(pointAltitudeContextEdits).length > 0)
        const fnApplyInternalProjectEdits = function (chunk) {
          const internalPaths = Object.keys(internalEdits)
          const fnSetByPathSafe = (typeof ctx.fnSetByPath === 'function') ? ctx.fnSetByPath : setByPath
          if (!internalPaths.length) return
          chunk.forEach((pt) => {
            internalPaths.forEach((path) => {
              fnSetByPathSafe(pt.props, path, internalEdits[path])
            })
            if (Object.prototype.hasOwnProperty.call(internalEdits, INTERNAL_POINT_NAME_PATH) && typeof ctx.fnSyncPointActionFileSuffixWithNameWithContext === 'function') {
              const nextName = ctx.fnGetPointName(pt)
              ctx.fnSyncPointActionFileSuffixWithNameWithContext(pt, nextName)
            }
            if (pt.marker && typeof pt.marker.setTooltipContent === 'function' && typeof ctx.fnGetWaypointTooltipText === 'function') {
              pt.marker.setTooltipContent(ctx.fnGetWaypointTooltipText(pt))
            }
            refreshWaypointMarkerSafe(pt, ctx, fallbackMissionState)
          })
        }
        return fnRunQueuedBulkTask(async function () {
          if (Object.keys(globalHeadingEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalHeadingEdits).forEach((path) => {
                applyKmzHeadingXmlEdit(ctx, pt, path, globalHeadingEdits[path])
              })
            })
            syncKmzHeadingScopesForPoints(points, ctx, { global: true })
          }
          if (Object.keys(globalSpeedEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalSpeedEdits).forEach((path) => {
                applyKmzSpeedXmlEdit(ctx, pt, path, globalSpeedEdits[path])
              })
            })
            syncKmzSpeedScopesForPoints(points, ctx, { global: true })
          }
          if (Object.keys(globalGimbalEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalGimbalEdits).forEach((path) => {
                applyKmzGimbalXmlEdit(ctx, pt, path, globalGimbalEdits[path])
              })
            })
            syncKmzGimbalScopesForPoints(points, ctx, { global: true })
          }
          if (Object.keys(globalPayloadEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalPayloadEdits).forEach((path) => {
                applyKmzPayloadXmlEdit(ctx, pt, path, globalPayloadEdits[path])
              })
            })
            syncKmzPayloadScopesForPoints(points, ctx, { global: true })
          }
          if (Object.keys(globalTurnEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalTurnEdits).forEach((path) => {
                applyKmzTurnXmlEdit(ctx, pt, path, globalTurnEdits[path])
              })
            })
            syncKmzTurnScopesForPoints(points, ctx, { global: true })
          }
          if (Object.keys(globalStraightLineEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalStraightLineEdits).forEach((path) => {
                applyKmzStraightLineXmlEdit(ctx, pt, path, globalStraightLineEdits[path])
              })
            })
            syncKmzStraightLineScopesForPoints(points, ctx, { global: true })
          }
          if (Object.keys(globalAltitudeContextEdits).length) {
            points.forEach((pt) => {
              Object.keys(globalAltitudeContextEdits).forEach((path) => {
                applyKmzAltitudeContextXmlEdit(ctx, pt, path, globalAltitudeContextEdits[path])
              })
            })
            syncKmzAltitudeScopesForPoints(points, ctx, { global: true })
          }
          await fnRunPointsWithProgress(points, function (chunk) {
            fnApplyInternalProjectEdits(chunk)
            if (Object.keys(pointHeadingEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointHeadingEdits).forEach((path) => {
                  applyKmzHeadingXmlEdit(ctx, pt, path, pointHeadingEdits[path])
                })
              })
            }
            if (Object.keys(pointSpeedEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointSpeedEdits).forEach((path) => {
                  applyKmzSpeedXmlEdit(ctx, pt, path, pointSpeedEdits[path])
                })
              })
            }
            if (Object.keys(pointGimbalEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointGimbalEdits).forEach((path) => {
                  applyKmzGimbalXmlEdit(ctx, pt, path, pointGimbalEdits[path])
                })
              })
            }
            if (Object.keys(pointPayloadEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointPayloadEdits).forEach((path) => {
                  applyKmzPayloadXmlEdit(ctx, pt, path, pointPayloadEdits[path])
                })
              })
            }
            if (Object.keys(pointTurnEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointTurnEdits).forEach((path) => {
                  applyKmzTurnXmlEdit(ctx, pt, path, pointTurnEdits[path])
                })
              })
            }
            if (Object.keys(pointStraightLineEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointStraightLineEdits).forEach((path) => {
                  applyKmzStraightLineXmlEdit(ctx, pt, path, pointStraightLineEdits[path])
                })
              })
            }
            if (Object.keys(pointAltitudeContextEdits).length) {
              chunk.forEach((pt) => {
                Object.keys(pointAltitudeContextEdits).forEach((path) => {
                  applyKmzAltitudeContextXmlEdit(ctx, pt, path, pointAltitudeContextEdits[path])
                })
              })
            }
            if (Object.keys(genericPointEdits).length) {
              ctx.fnApplyEditsToPoints(chunk, genericPointEdits)
              fnSyncAltitudeAfterBulkEdits(chunk, genericPointEdits)
            }
            if (hasHeadingEdits) {
              syncKmzHeadingScopesForPoints(chunk, ctx)
            }
            if (hasSpeedEdits) {
              syncKmzSpeedScopesForPoints(chunk, ctx)
            }
            if (hasGimbalEdits) {
              syncKmzGimbalScopesForPoints(chunk, ctx)
            }
            if (hasPayloadEdits) {
              syncKmzPayloadScopesForPoints(chunk, ctx)
            }
            if (hasTurnEdits) {
              syncKmzTurnScopesForPoints(chunk, ctx)
            }
            if (hasStraightLineEdits) {
              syncKmzStraightLineScopesForPoints(chunk, ctx)
            }
            if (hasAltitudeContextEdits) {
              syncKmzAltitudeScopesForPoints(chunk, ctx)
            }
          })
          if (hasHeadingEdits || hasAltitudeContextEdits) {
            points.forEach((pt) => {
              refreshWaypointMarkerSafe(pt, ctx, fallbackMissionState)
            })
          }
          if (
            Object.keys(internalEdits).length ||
            Object.keys(globalHeadingEdits).length ||
            Object.keys(globalSpeedEdits).length ||
            Object.keys(globalGimbalEdits).length ||
            Object.keys(globalTurnEdits).length ||
            Object.keys(globalAltitudeContextEdits).length ||
            hasHeadingEdits ||
            hasSpeedEdits ||
            hasGimbalEdits ||
            hasTurnEdits ||
            hasAltitudeContextEdits
          ) {
            scheduleRoute3dSharedSyncSafe(ctx)
          }
          hasCommittedChanges = true
          fnCommitBulkHistoryIfNeeded()
          fnClearAppliedDirtyFlags(dirtyInputs)
        })
      }

      const fnQueueBulkActionApply = function (applyPerPoint) {
        return fnRunQueuedBulkTask(async function () {
          await fnRunPointsWithProgress(points, function (chunk) {
            chunk.forEach((pt) => {
              applyPerPoint(pt)
            })
          })
          scheduleRoute3dSharedSyncSafe(ctx)
          hasCommittedChanges = true
          fnCommitBulkHistoryIfNeeded()
        })
      }

      const fnHasPendingBulkChanges = function () {
        const pending = fnCollectDirtyEdits()
        return !!Object.keys(pending.edits).length || hasPendingActionChanges
      }

      const fnCloseBulkModal = function () {
        allowModalClose = true
        ctx.areaSwal.close($popup.get(0))
      }

      const fnAttemptCloseWithoutSaving = async function () {
        if (!fnHasPendingBulkChanges()) {
          fnCloseBulkModal()
          return
        }
        const result = await ctx.areaSwal.fire({
          title: ctx.fnT('bulk.unsaved_changes_title'),
          text: ctx.fnT('bulk.unsaved_changes_text'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: ctx.fnT('bulk.unsaved_changes_confirm'),
          cancelButtonText: ctx.fnT('common.back')
        })
        if (result && result.isConfirmed) {
          fnCloseBulkModal()
        }
      }

      const fnReplaceModalCloseHandler = function (selector) {
        const $oldBtn = $popup.find(selector).first()
        if (!$oldBtn.length) return
        const $newBtn = $oldBtn.clone(false)
        $oldBtn.replaceWith($newBtn)
        $newBtn.on('click', function (event) {
          event.preventDefault()
          event.stopPropagation()
          void fnAttemptCloseWithoutSaving()
        })
      }

      $popup.find('.swal2-confirm').first()
        .removeClass('btn-primary')
        .addClass('btn-outline-primary')
      $popup.find('.swal2-deny').first()
        .removeClass('btn-warning')
        .addClass('btn-primary')
      fnReplaceModalCloseHandler('.swal2-cancel')
      fnReplaceModalCloseHandler('.swal2-close')

      fnApplyPendingBulkChanges = async function () {
        let didApply = false
        const pending = fnCollectDirtyEdits()
        const hasPendingPropertyEdits = Object.keys(pending.edits).length > 0
        const hasPendingBulkChanges = hasPendingPropertyEdits || hasPendingActionChanges
        if (!hasPendingBulkChanges) {
          fnRenderProgress(0, 0, false, 'bulk.progress_idle')
          return false
        }
        beginRoute3dSharedSyncSuspend()
        try {
          if (hasPendingPropertyEdits) {
            await fnQueueBulkPropertyApply(pending.edits, pending.dirtyInputs)
            didApply = true
          }
          if (hasPendingActionChanges) {
            if (ctx.importMissionType !== 'kmz') {
              const nextActions = ctx._.cloneDeep(Array.isArray(kmlBulkActionsDraft) ? kmlBulkActionsDraft : [])
              await fnQueueBulkActionApply(function (pt) {
                ctx.fnSetPointKmlActionsWithContext(pt, nextActions)
              })
            } else {
              await fnQueueBulkActionApply(function (pt) {
                const perPointGroups = ctx.fnResolveKmzActionFileSuffixPointNameTokensInGroups(kmzBulkGroupsDraft, function () {
                  return ctx.fnGetPointName(pt)
                })
                ctx.fnSetPointKmzActionGroupsWithContext(pt, perPointGroups, {
                  alignReachPointRangeToIndex: true,
                  alignBetweenAdjacentPointsRangeToIndex: true,
                  ensureMultipleTimingTriggerParam: true
                })
              })
            }
            hasPendingActionChanges = false
            didApply = true
          }
          await bulkTaskTail.catch(function () {})
        } finally {
          endRoute3dSharedSyncSuspend()
        }
        if (didApply) {
          await syncRoute3dSharedStateSafe(ctx)
        }
        if (!didApply) {
          fnRenderProgress(0, 0, false, 'bulk.progress_idle')
        }
        return didApply
      }

      fnRenderProgress(0, 0, false, 'bulk.progress_idle')

      $inputs.on('input', function () {
        const $input = ctx.$(this)
        $input.data('dirty', true)
        runBulkAltitudePreviewSync($cont, $inputs, $input, points, ctx)
      })
      $inputs.on('change', function () {
        const $input = ctx.$(this)
        runBulkAltitudePreviewSync($cont, $inputs, $input, points, ctx)
      })

      if (ctx.importMissionType !== 'kmz') {
        const $actionsCont = ctx.$('<div class="mt-2"></div>')
        $cont.append($actionsCont)
        const initialActions = ctx.fnBuildSharedKmlActionsForPoints(points)
        renderKmlActionsEditor($actionsCont, initialActions, function (nextActions) {
          kmlBulkActionsDraft = ctx._.cloneDeep(Array.isArray(nextActions) ? nextActions : [])
          hasPendingActionChanges = true
        })
      } else {
        const $actionsCont = ctx.$('<div class="mt-2"></div>')
        $cont.append($actionsCont)
        const initialGroups = ctx.fnBuildSharedKmzActionGroupsForPoints(points)
        renderKmzActionGroupsEditor($actionsCont, initialGroups, function (nextGroups) {
          kmzBulkGroupsDraft = ctx._.cloneDeep(Array.isArray(nextGroups) ? nextGroups : [])
          hasPendingActionChanges = true
        }, {
          fileSuffixMode: 'token',
          defaultPointIndex: Number.isFinite(Number(ctx._.get(points[0], 'idx'))) ? Number(ctx._.get(points[0], 'idx')) : 0,
          emitInitial: false
        })
      }
    },
    preConfirm: async function () {
      await fnApplyPendingBulkChanges()
      return false
    },
    preDeny: async function () {
      await fnApplyPendingBulkChanges()
      allowModalClose = true
      return true
    }
  })
}

export {
  syncVisibleKmzActionFileSuffixInputs,
  renderPortalPropEditorRows,
  openPointPropsSwal,
  openBulkEditSwal
}
